import React, { useState } from "react";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { storage, db } from "./firebase";
import "./document-manager.css";

// Constants
const DEFAULT_REVISION = "Rev 0";

/**
 * DocumentManager - A Windows-like file system interface for managing RFQ documents with revision support
 * 
 * Features:
 * - Hierarchical folder structure organized by revisions
 * - File type icons and metadata display
 * - Upload, delete, and file operations
 * - Backward compatibility with existing flat file structure
 */
export default function DocumentManager({ tenderId, tender, onTenderUpdate }) {
  const [expandedFolders, setExpandedFolders] = useState(new Set(["root"]));
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadingTo, setUploadingTo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [newRevisionName, setNewRevisionName] = useState("");
  const [notification, setNotification] = useState(null);

  // Show notification and auto-hide after 3 seconds
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Get or create default revision structure
  const getRevisionStructure = () => {
    const revisions = tender?.revisions || [];
    const files = tender?.files || [];

    // Create folder structure
    const structure = {
      root: {
        name: "Documents",
        type: "folder",
        children: {}
      }
    };

    // Add revision folders
    if (revisions.length === 0) {
      // If no revisions exist, create default revision folder
      structure.root.children[DEFAULT_REVISION] = {
        name: DEFAULT_REVISION,
        type: "folder",
        children: {},
        metadata: { timestamp: new Date().toISOString() }
      };
    } else {
      revisions.forEach(rev => {
        structure.root.children[rev.name] = {
          name: rev.name,
          type: "folder",
          children: {},
          metadata: { timestamp: rev.timestamp }
        };
      });
    }

    // Organize files into revision folders
    files.forEach(file => {
      const revisionName = file.revision || DEFAULT_REVISION; // Default for backward compatibility
      
      if (!structure.root.children[revisionName]) {
        // Create folder if it doesn't exist (for backward compatibility)
        structure.root.children[revisionName] = {
          name: revisionName,
          type: "folder",
          children: {},
          metadata: { timestamp: new Date().toISOString() }
        };
      }

      structure.root.children[revisionName].children[file.name] = {
        name: file.name,
        type: "file",
        url: file.url,
        size: file.size,
        revision: revisionName,
        metadata: file
      };
    });

    return structure;
  };

  const structure = getRevisionStructure();

  // Get file type icon
  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    const iconMap = {
      pdf: "📄",
      doc: "📝",
      docx: "📝",
      xls: "📊",
      xlsx: "📊",
      csv: "📊",
      txt: "📃",
      zip: "🗜️",
      rar: "🗜️",
      jpg: "🖼️",
      jpeg: "🖼️",
      png: "🖼️",
      gif: "🖼️"
    };
    return iconMap[ext] || "📎";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Toggle folder expansion
  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  // Handle file upload
  const handleUpload = async (files, revisionName) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setUploadingTo(revisionName);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Store in revision-specific folder
        const filePath = `tenders/${tenderId}/${revisionName}/${file.name}`;
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        return {
          name: file.name,
          url,
          size: file.size,
          revision: revisionName,
          uploadedAt: new Date().toISOString()
        };
      });

      const newFiles = await Promise.all(uploadPromises);
      
      // Update Firestore
      const tenderRef = doc(db, "tenders", tenderId);
      await updateDoc(tenderRef, {
        files: arrayUnion(...newFiles)
      });

      // Notify parent component
      if (onTenderUpdate) {
        onTenderUpdate();
      }

      showNotification(`Successfully uploaded ${newFiles.length} file(s) to ${revisionName}`, "success");
    } catch (error) {
      console.error("Upload error:", error);
      showNotification("Error uploading files: " + error.message, "error");
    } finally {
      setIsUploading(false);
      setUploadingTo(null);
    }
  };

  // Handle file deletion
  const handleDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete ${file.name}?`)) return;

    try {
      // Delete from Storage - parse URL carefully
      let urlPath;
      try {
        const urlParts = file.url.split("/o/");
        if (urlParts.length < 2) {
          throw new Error("Invalid Firebase Storage URL format");
        }
        urlPath = decodeURIComponent(urlParts[1].split("?")[0]);
      } catch (parseError) {
        console.error("Error parsing storage URL:", parseError);
        throw new Error("Failed to parse file URL. Please try again.");
      }

      const fileRef = ref(storage, urlPath);
      await deleteObject(fileRef).catch((storageError) => {
        // File might not exist in storage, log but continue with Firestore deletion
        console.warn("File not found in storage, continuing with Firestore deletion:", storageError);
      });

      // Delete from Firestore
      const tenderRef = doc(db, "tenders", tenderId);
      await updateDoc(tenderRef, {
        files: arrayRemove(file.metadata)
      });

      // Notify parent component
      if (onTenderUpdate) {
        onTenderUpdate();
      }

      setSelectedItem(null);
      showNotification("File deleted successfully", "success");
    } catch (error) {
      console.error("Delete error:", error);
      showNotification("Error deleting file: " + error.message, "error");
    }
  };

  // Handle folder (revision) deletion
  const handleDeleteFolder = async (folder) => {
    const filesInFolder = Object.values(folder.children || {});
    const fileCount = filesInFolder.length;
    
    // Confirm deletion with file count warning
    const confirmMessage = fileCount > 0
      ? `Are you sure you want to delete "${folder.name}" and all ${fileCount} file(s) inside it?`
      : `Are you sure you want to delete the empty folder "${folder.name}"?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete all files in the folder from Firebase Storage
      const deletePromises = filesInFolder.map(async (file) => {
        try {
          const urlParts = file.url.split("/o/");
          if (urlParts.length >= 2) {
            const urlPath = decodeURIComponent(urlParts[1].split("?")[0]);
            const fileRef = ref(storage, urlPath);
            await deleteObject(fileRef).catch((storageError) => {
              console.warn(`File not found in storage (${file.name}), continuing:`, storageError);
            });
          }
        } catch (parseError) {
          console.warn(`Error parsing URL for file ${file.name}:`, parseError);
        }
      });

      await Promise.all(deletePromises);

      // Remove all files in this folder from Firestore
      const tenderRef = doc(db, "tenders", tenderId);
      const filesToRemove = filesInFolder.map(file => file.metadata);
      
      // Build update object
      const updates = {};
      
      // Remove files if any
      if (filesToRemove.length > 0) {
        // We need to remove files one by one using arrayRemove
        // But for efficiency, we'll fetch and filter
        const currentFiles = tender?.files || [];
        const updatedFiles = currentFiles.filter(
          f => f.revision !== folder.name
        );
        updates.files = updatedFiles;
      }

      // Remove the revision from revisions array
      const currentRevisions = tender?.revisions || [];
      const revisionToRemove = currentRevisions.find(r => r.name === folder.name);
      if (revisionToRemove) {
        updates.revisions = currentRevisions.filter(r => r.name !== folder.name);
      }

      // Update Firestore
      await updateDoc(tenderRef, updates);

      // Notify parent component
      if (onTenderUpdate) {
        onTenderUpdate();
      }

      setSelectedItem(null);
      showNotification(`Folder "${folder.name}" deleted successfully`, "success");
    } catch (error) {
      console.error("Delete folder error:", error);
      showNotification("Error deleting folder: " + error.message, "error");
    }
  };

  // Handle creating a new revision
  const handleCreateRevision = async () => {
    if (!newRevisionName.trim()) {
      showNotification("Please enter a revision name", "error");
      return;
    }

    try {
      const tenderRef = doc(db, "tenders", tenderId);
      const newRevision = {
        name: newRevisionName.trim(),
        timestamp: new Date().toISOString()
      };
      
      await updateDoc(tenderRef, {
        revisions: arrayUnion(newRevision)
      });

      // Notify parent component
      if (onTenderUpdate) {
        onTenderUpdate();
      }

      setShowRevisionDialog(false);
      setNewRevisionName("");
      showNotification(`Revision "${newRevision.name}" created successfully`, "success");
      
      // Expand the new revision folder
      setExpandedFolders(prev => new Set([...prev, `root/${newRevision.name}`]));
    } catch (error) {
      console.error("Error creating revision:", error);
      showNotification("Error creating revision: " + error.message, "error");
    }
  };

  // Render folder tree recursively
  const renderTree = (node, path = "root", level = 0) => {
    const isExpanded = expandedFolders.has(path);
    const isSelected = selectedItem === path;

    if (node.type === "folder") {
      const childEntries = Object.entries(node.children || {});
      const hasChildren = childEntries.length > 0;

      return (
        <div key={path} style={{ marginLeft: level * 20 }}>
          <div
            className={`tree-item ${isSelected ? "selected" : ""}`}
            onClick={() => {
              toggleFolder(path);
              setSelectedItem(path);
            }}
          >
            <span className="tree-icon">
              {hasChildren ? (isExpanded ? "📂" : "📁") : "📁"}
            </span>
            <span className="tree-label">{node.name}</span>
            {node.metadata?.timestamp && (
              <span className="tree-meta">
                {new Date(node.metadata.timestamp).toLocaleDateString()}
              </span>
            )}
          </div>

          {isExpanded && hasChildren && (
            <div className="tree-children">
              {childEntries
                .sort((a, b) => {
                  // Folders first, then files
                  if (a[1].type === "folder" && b[1].type === "file") return -1;
                  if (a[1].type === "file" && b[1].type === "folder") return 1;
                  return a[0].localeCompare(b[0]);
                })
                .map(([key, child]) =>
                  renderTree(child, `${path}/${key}`, level + 1)
                )}
            </div>
          )}
        </div>
      );
    } else {
      // File node
      return (
        <div
          key={path}
          className={`tree-item tree-file ${isSelected ? "selected" : ""}`}
          style={{ marginLeft: level * 20 }}
          onClick={() => setSelectedItem(path)}
        >
          <span className="tree-icon">{getFileIcon(node.name)}</span>
          <span className="tree-label">{node.name}</span>
          <span className="tree-meta">{formatFileSize(node.size)}</span>
        </div>
      );
    }
  };

  // Get selected item details
  const getSelectedItemDetails = () => {
    if (!selectedItem) return null;

    const parts = selectedItem.split("/");
    let current = structure.root;

    for (let i = 1; i < parts.length; i++) {
      if (current.children && current.children[parts[i]]) {
        current = current.children[parts[i]];
      } else {
        return null;
      }
    }

    return current;
  };

  const selectedDetails = getSelectedItemDetails();
  const isRootSelected = selectedItem === "root";
  const isRevisionFolder = selectedDetails?.type === "folder" && selectedItem !== "root";

  return (
    <div className="document-manager">
      <div className="dm-toolbar">
        <h3 style={{ margin: 0 }}>Document Manager</h3>
        <div className="dm-toolbar-actions">
          <button
            className="dm-new-revision-btn"
            onClick={() => setShowRevisionDialog(true)}
          >
            📁 New Revision
          </button>
          {isRevisionFolder && (
            <label className="dm-upload-btn">
              <input
                type="file"
                multiple
                onChange={(e) => handleUpload(e.target.files, selectedDetails.name)}
                style={{ display: "none" }}
                disabled={isUploading}
              />
              {isUploading && uploadingTo === selectedDetails.name
                ? "⏳ Uploading..."
                : "📤 Upload to this folder"}
            </label>
          )}
        </div>
      </div>

      {/* Revision Creation Dialog */}
      {showRevisionDialog && (
        <div className="dm-modal-overlay" onClick={() => setShowRevisionDialog(false)}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h4>Create New Revision</h4>
              <button
                className="dm-modal-close"
                onClick={() => setShowRevisionDialog(false)}
              >
                ×
              </button>
            </div>
            <div className="dm-modal-content">
              <label className="dm-modal-label">Revision Name:</label>
              <input
                type="text"
                className="dm-modal-input"
                placeholder="e.g., Rev 1, Rev 2, Final Version, etc."
                value={newRevisionName}
                onChange={(e) => setNewRevisionName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleCreateRevision()}
                autoFocus
              />
              <p className="dm-modal-hint">
                💡 Create revision folders to organize your RFQ documents by version
              </p>
            </div>
            <div className="dm-modal-footer">
              <button
                className="dm-modal-btn dm-modal-btn-cancel"
                onClick={() => setShowRevisionDialog(false)}
              >
                Cancel
              </button>
              <button
                className="dm-modal-btn dm-modal-btn-primary"
                onClick={handleCreateRevision}
              >
                Create Revision
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dm-content">
        <div className="dm-tree-panel">
          <div className="dm-tree">
            {renderTree(structure.root)}
          </div>
        </div>

        <div className="dm-details-panel">
          {selectedDetails ? (
            <div className="dm-details">
              <div className="dm-details-header">
                <span className="dm-details-icon">
                  {selectedDetails.type === "folder" ? "📁" : getFileIcon(selectedDetails.name)}
                </span>
                <h4>{selectedDetails.name}</h4>
              </div>

              {selectedDetails.type === "file" && (
                <div className="dm-details-content">
                  <div className="dm-detail-row">
                    <span className="dm-detail-label">Size:</span>
                    <span className="dm-detail-value">{formatFileSize(selectedDetails.size)}</span>
                  </div>
                  <div className="dm-detail-row">
                    <span className="dm-detail-label">Revision:</span>
                    <span className="dm-detail-value">{selectedDetails.revision}</span>
                  </div>
                  {selectedDetails.metadata?.uploadedAt && (
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Uploaded:</span>
                      <span className="dm-detail-value">
                        {new Date(selectedDetails.metadata.uploadedAt).toLocaleString()}
                      </span>
                    </div>
                  )}

                  <div className="dm-actions">
                    <a
                      href={selectedDetails.url}
                      target="_blank"
                      rel="noreferrer"
                      className="dm-action-btn dm-action-primary"
                    >
                      📥 Download
                    </a>
                    <a
                      href={selectedDetails.url}
                      target="_blank"
                      rel="noreferrer"
                      className="dm-action-btn dm-action-secondary"
                    >
                      👁️ View
                    </a>
                    <button
                      onClick={() => handleDelete(selectedDetails)}
                      className="dm-action-btn dm-action-danger"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              )}

              {selectedDetails.type === "folder" && !isRootSelected && (
                <div className="dm-details-content">
                  <div className="dm-detail-row">
                    <span className="dm-detail-label">Type:</span>
                    <span className="dm-detail-value">Revision Folder</span>
                  </div>
                  {selectedDetails.metadata?.timestamp && (
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Created:</span>
                      <span className="dm-detail-value">
                        {new Date(selectedDetails.metadata.timestamp).toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="dm-detail-row">
                    <span className="dm-detail-label">Files:</span>
                    <span className="dm-detail-value">
                      {Object.keys(selectedDetails.children || {}).length}
                    </span>
                  </div>

                  <div className="dm-folder-help">
                    <p>💡 Upload documents to this revision folder using the button above.</p>
                  </div>

                  <div className="dm-actions">
                    <button
                      onClick={() => handleDeleteFolder(selectedDetails)}
                      className="dm-action-btn dm-action-danger"
                    >
                      🗑️ Delete Folder
                    </button>
                  </div>
                </div>
              )}

              {isRootSelected && (
                <div className="dm-details-content">
                  <p className="dm-welcome-text">
                    Select a revision folder or file from the tree to view details.
                  </p>
                  <div className="dm-help">
                    <h5>📖 How to use Document Manager:</h5>
                    <ul>
                      <li>📁 Click on revision folders to expand/collapse them</li>
                      <li>📤 Select a revision folder and click "Upload" to add documents</li>
                      <li>📄 Click on files to view details and perform actions</li>
                      <li>🗑️ Delete files by selecting them and clicking Delete</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="dm-details-empty">
              <p>Select an item to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className={`dm-notification dm-notification-${notification.type}`}>
          {notification.type === "success" ? "✓" : "⚠"} {notification.message}
        </div>
      )}
    </div>
  );
}

